import { formatLabelWithCode } from '../../../../utils/locationFormat';
import type { Game, GameStateMeta } from '../../../../types/features/games';
import type { CountryStatesGroup, SimpleState } from '../../../../types/locations';

export const buildStatesByCountry = (groupedStates: CountryStatesGroup[]): Map<string, SimpleState[]> => {
  const statesByCountry = new Map<string, SimpleState[]>();

  groupedStates.forEach((group) => {
    statesByCountry.set(group.country_code, group.states);
  });

  return statesByCountry;
};

export const buildStateMetaByCode = (groupedStates: CountryStatesGroup[]): Map<string, GameStateMeta> => {
  const stateMetaByCode = new Map<string, GameStateMeta>();

  groupedStates.forEach((group) => {
    group.states.forEach((state) => {
      stateMetaByCode.set(state.state_code, {
        state,
        country_code: group.country_code,
        country_name: group.country_name || '',
      });
    });
  });

  return stateMetaByCode;
};

export const getStateLabel = (stateId: string | undefined, selectedStateDetails?: GameStateMeta): string =>
  formatLabelWithCode(selectedStateDetails?.state.state_name, selectedStateDetails?.state.state_code || stateId);

const escapeCsvValue = (value: unknown): string => {
  const stringValue = value == null ? '' : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

export const createGamesCsvContent = (games: Game[]): string => {
  const headers = [
    'id',
    'game_id',
    'name',
    'state',
    'state_code',
    'country_code',
    'plu',
    'tickets_per_pack',
    'cost_per_ticket',
    'start_date',
    'end_date',
    'image_url',
    'created_at',
    'updated_at',
  ];

  const rows = games.map((game) => [
    game.id,
    game.game_id,
    game.name,
    game.state,
    game.state_code,
    game.country_code,
    game.plu,
    game.tickets_per_pack,
    game.cost_per_ticket,
    game.start_date,
    game.end_date,
    game.image_url,
    game.created_at,
    game.updated_at,
  ]);

  return [headers.join(','), ...rows.map((row) => row.map(escapeCsvValue).join(','))].join('\r\n');
};

export const getGamesCsvFileName = (stateId: string | undefined, selectedStateDetails?: GameStateMeta): string => {
  const countryCode = (selectedStateDetails?.country_code || 'country').toUpperCase();
  const stateCode = (selectedStateDetails?.state.state_code || stateId || 'state').toUpperCase();
  return `games_${countryCode}_${stateCode}.csv`;
};
